// Copyright ©️ 2026 eVolve MEP, LLC

import React, { type ReactNode, useEffect } from 'react';

import { Flex, type MantineSpacing, Card } from '@mantine/core';

import { GlobalFilters } from 'components/Mantine/GlobalFilters/GlobalFilters';
import { isNotNil } from 'helpers/isNotNil';

import { PageBreadcrumb, type PageBreadcrumbProps } from './Navigation/PageBreadcrumb';

export type BasePageHeaderProps = {
  spacing?: MantineSpacing;
  topLeftComponent?: ReactNode;
  topRightComponent?: ReactNode;
  bottomLeftComponent?: ReactNode;
  bottomRightComponent?: ReactNode;
  /** If set, will hide the `<GlobalFilters />` component and related Project/Facility filters. */
  hideGlobalFilters?: boolean;
} & PageBreadcrumbProps;

export const useSetDocumentTitle = (title: string) => {
  useEffect(() => {
    document.title = `${title ? `${title} – ` : ''}EVOLVE`;
  }, [title]);
};

export const BasePageHeader = ({
  spacing = 'xs',
  topLeftComponent,
  topRightComponent,
  bottomLeftComponent,
  bottomRightComponent,
  hideGlobalFilters,
  ...props
}: BasePageHeaderProps) => {
  useSetDocumentTitle(props.title);

  const isDetailPage = isNotNil(props.parentPage);

  const leftSectionMiw = 150;
  const rightSectionMiw = 300;

  return (
    <>
      <Card
        display="flex"
        mb={spacing}
        mih={isDetailPage ? 50 : 36}
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: isDetailPage ? 'center' : 'flex-start',
          gap: 'var(--mantine-spacing-md)',
          flexWrap: 'wrap',
          overflow: 'visible',
        }}
        withBorder={isDetailPage}
        py={6}
        px={isDetailPage ? 'md' : 0}
        radius={isDetailPage ? 'md' : 0}
        bg={isDetailPage ? 'gray.1' : 'transparent'}
      >
        <Flex align="center" gap="md" miw={leftSectionMiw}>
          <PageBreadcrumb {...props} />
          {topLeftComponent}
        </Flex>
        <Flex align="center" justify="flex-end" wrap="wrap" gap="md" ml="auto" miw={rightSectionMiw}>
          {topRightComponent}
          {!hideGlobalFilters && <GlobalFilters />}
        </Flex>
      </Card>
      <Flex justify="space-between" align="flex-end" mb="xs" gap="lg" wrap="wrap">
        <Flex align="center" gap="md" miw={leftSectionMiw}>
          {bottomLeftComponent}
        </Flex>
        <Flex align="center" justify="flex-end" gap="md" ml="auto" miw={rightSectionMiw}>
          {bottomRightComponent}
        </Flex>
      </Flex>
    </>
  );
};
